import Head from "next/head";
import { useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import dynamic from "next/dynamic";
import {
  HandThumbUpIcon,
  CpuChipIcon,
  FlagIcon,
  BoltIcon,
  LinkIcon,
  UserGroupIcon,
  ChartPieIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import { SITE_URL } from "@/lib/constants";

const NewHeader = dynamic(
  () => import("@/components/NewHeader").then((mod) => mod.default),
  { ssr: true }
);
const Footer = dynamic(
  () => import("@/components/homepages/Footer").then((mod) => mod.default),
  { ssr: true }
);
const CallToAction = dynamic(
  () =>
    import("@/components/homepages/CallToAction").then(
      (mod) => mod.CallToAction
    ),
  { ssr: true }
);
const ComparisonHero = dynamic(
  () => import("@/components/comparison/ComparisonHero"),
  { ssr: true }
);
const ComparisonIntro = dynamic(
  () => import("@/components/comparison/ComparisonIntro"),
  { ssr: true }
);
const ComparisonDifferences = dynamic(
  () => import("@/components/comparison/ComparisonDifferences"),
  { ssr: true }
);
const ComparisonTable = dynamic(
  () => import("@/components/comparison/ComparisonTable"),
  { ssr: true }
);
const ComparisonPricing = dynamic(
  () => import("@/components/comparison/ComparisonPricing"),
  { ssr: true }
);
const ComparisonTestimonial = dynamic(
  () => import("@/components/comparison/ComparisonTestimonial"),
  { ssr: true }
);
const ComparisonBenefits = dynamic(
  () => import("@/components/comparison/ComparisonBenefits"),
  { ssr: true }
);
const ComparisonFAQ = dynamic(
  () => import("@/components/comparison/ComparisonFAQ"),
  { ssr: true }
);

// ─── Data ──────────────────────────────────────────────

const introCards = [
  {
    label: "LEDGEROWL",
    labelColor: "text-blue-primary-500",
    icon: <CpuChipIcon className="w-4 h-4" />,
    title: "Real-time accounting software + service",
    description:
      "Tailored for SMEs and growing businesses — delivering continuous financial updates, automated compliance, and native integrations with your existing tools.",
  },
  {
    label: "OUTSOURCING",
    labelColor: "text-gray-cool-500",
    icon: <DocumentTextIcon className="w-4 h-4" />,
    title: "Manual processes with generic tools",
    description:
      "Generally rely on QuickBooks or Xero — generic tools not built for modern businesses. The result is often delayed, generic books that don't provide sufficient data for making smarter decisions.",
  },
];

const coreDifferences = [
  {
    icon: <FlagIcon className="w-5 h-5" />,
    title: "Market Focus",
    description:
      "Ledgerowl is purpose-built for SMEs and growing businesses; outsourced bookkeepers use one-size-fits-all approaches across industries.",
  },
  {
    icon: <ChartBarIcon className="w-5 h-5" />,
    title: "Real-Time Reporting",
    description:
      "Ledgerowl delivers real-time financial visibility; outsourced bookkeepers typically deliver reports 15+ days after month-end.",
  },
  {
    icon: <BoltIcon className="w-5 h-5" />,
    title: "Technology",
    description:
      "Ledgerowl uses proprietary AI-powered automation native to the platform; outsourced bookkeepers rely on manual processes in Xero or QuickBooks.",
  },
  {
    icon: <LinkIcon className="w-5 h-5" />,
    title: "Integrations",
    description:
      "Ledgerowl provides native integrations with your existing tools; outsourced bookkeepers use disconnected third-party add-ons.",
  },
  {
    icon: <CpuChipIcon className="w-5 h-5" />,
    title: "Automation",
    description:
      "Ledgerowl automates reconciliation, categorisation, and compliance workflows; outsourced bookkeepers do it all manually.",
  },
];

const comparisonRows = [
  {
    feature: "Financial Reporting",
    leftStatus: "check",
    leftDetail: "Real-time dashboard & reports",
    rightStatus: "check",
    rightDetail: "Monthly or quarterly, often delayed",
  },
  {
    feature: "Accounting Support",
    leftStatus: "check",
    leftDetail: "Dedicated team + AI-powered tools",
    rightStatus: "check",
    rightDetail: "Manual support only",
  },
  {
    feature: "Built for SMEs & Growing Businesses",
    leftStatus: "check",
    leftDetail: "Purpose-built for scale",
    rightStatus: "cross",
    rightDetail: "Generic, serves all industries",
  },
  {
    feature: "Native Integrations",
    leftStatus: "check",
    leftDetail: "Deep integrations with banks, payroll & apps",
    rightStatus: "cross",
    rightDetail: "Relies on Xero/QBO with third-party add-ons",
  },
  {
    feature: "Real-Time Data Syncing",
    leftStatus: "check",
    leftDetail: "Continuous sync with your sources of truth",
    rightStatus: "cross",
    rightDetail: "Subject to delays & manual reconciliation",
  },
  {
    feature: "AI-Powered Automation",
    leftStatus: "check",
    leftDetail: "Smart categorisation & anomaly detection",
    rightStatus: "cross",
    rightDetail: "Manual processes & data entry",
  },
  {
    feature: "Compliance & Tax-Ready",
    leftStatus: "check",
    leftDetail: "Always audit-ready with automated compliance",
    rightStatus: "cross",
    rightDetail: "Requires separate tax prep & review",
  },
];

const benefits = [
  {
    icon: <CpuChipIcon className="w-6 h-6" />,
    title: "AI-Powered Books",
    description: "100% accuracy, available 24/7, powered by intelligent automation",
  },
  {
    icon: <UserGroupIcon className="w-6 h-6" />,
    title: "Dedicated Team",
    description: "A dedicated accounting team, 7 days a week",
  },
  {
    icon: <ChartPieIcon className="w-6 h-6" />,
    title: "Real-Time Dashboards",
    description: "KPIs & financial insights in one dashboard",
  },
  {
    icon: <ShieldCheckIcon className="w-6 h-6" />,
    title: "Tax-Ready Reports",
    description: "AI-powered reconciliation & categorisation",
  },
  {
    icon: <LinkIcon className="w-6 h-6" />,
    title: "Seamless Integrations",
    description: "Connect your entire financial ecosystem in minutes",
  },
];

const faqItems = [
  {
    question: "When should I switch from outsourced bookkeeping to Ledgerowl?",
    answer:
      "If you're spending too much time waiting for reports, reconciling data manually, or struggling with software that wasn't built for your business — it's time to switch. Ledgerowl is ideal for businesses ready to scale with real-time financial visibility.",
  },
  {
    question:
      "What makes Ledgerowl different from using Xero or QuickBooks with an outsourced bookkeeper?",
    answer:
      "Outsourced bookkeepers use generic tools like Xero or QuickBooks, which require manual data entry and third-party integrations. Ledgerowl combines purpose-built software with expert services, giving you automation, real-time data, and a dedicated team — all in one platform.",
  },
  {
    question: "Can Ledgerowl work alongside my existing accountant?",
    answer:
      "Absolutely. Many firms and accountants partner with Ledgerowl to leverage our real-time financials, automated reconciliation, and compliance tools — enabling them to focus on strategic advisory rather than data entry.",
  },
  {
    question: "Is Ledgerowl more cost-effective than outsourced bookkeeping?",
    answer:
      "Yes. Outsourced bookkeeping often requires separate costs for the bookkeeper, accounting software, and integration tools. Ledgerowl bundles software, integrations, and expert services into one transparent price — typically saving businesses 40-60% compared to traditional setups.",
  },
  {
    question: "How long does it take to onboard with Ledgerowl?",
    answer:
      "Most businesses are fully onboarded within days, not weeks. Our native integrations pull in your data automatically, and our team handles the migration so you don't have to lift a finger.",
  },
];

const integrationLogos = [
  { name: "Xero", logo: "/logos/xero.svg", width: 80, height: 32 },
  { name: "MYOB", logo: "/logos/myob.svg", width: 80, height: 32 },
  { name: "Shopify", logo: "/logos/shopify.svg", width: 100, height: 32 },
  { name: "Stripe", logo: "/logos/stripe.svg", width: 80, height: 32 },
  { name: "Square", logo: "/logos/square.svg", width: 90, height: 32 },
  { name: "CommBank", logo: "/logos/commbank.svg", width: 120, height: 32 },
  { name: "ANZ", logo: "/logos/anz.svg", width: 70, height: 32 },
  { name: "PayPal", logo: "/logos/paypal.svg", width: 100, height: 32 },
];

// ─── Page ──────────────────────────────────────────────

export default function VsOutsourcing() {
  // Scroll-triggered fade-in animations
  useEffect(() => {
    if (typeof window === "undefined") return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    document.querySelectorAll(".fade-in:not(.visible)").forEach((el) => io.observe(el));

    const mo = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.classList?.contains("fade-in") && !node.classList.contains("visible")) {
            io.observe(node);
          }
          node.querySelectorAll?.(".fade-in:not(.visible)").forEach((el) => io.observe(el));
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, []);

  return (
    <>
      <Head>
        <title>Ledgerowl vs Outsourced Bookkeeping | AI Bookkeeper Australia</title>
        <meta
          name="description"
          content="Compare Ledgerowl's AI bookkeeping with traditional outsourced bookkeeping services. Faster, more accurate, human-verified accounting for Australian businesses."
        />
        <meta
          property="og:title"
          content="Ledgerowl vs Outsourced Bookkeeping | AI Bookkeeper Australia"
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/vs-outsourcing`} />
        <meta property="og:image" content="https://ledgerowl.com.au/favicon.png" />
        <meta property="og:site_name" content="Ledgerowl" />
        <meta property="og:locale" content="en_AU" />
        <meta
          name="keywords"
          content="AI bookkeeping Australia, outsourced bookkeeper alternative, fixed fee bookkeeping Australia, human verified bookkeeping Australia, bookkeeping service with accountant Australia, small business bookkeeper Sydney"
        />
        <link rel="canonical" href={`${SITE_URL}/vs-outsourcing`} />
        <link rel="icon" href="/favicon.png" />
      </Head>

      <NewHeader />

      <ComparisonHero
        title={
          <>
            <span className="text-blue-primary-500">Ledgerowl</span> vs.
            Outsourcing
          </>
        }
        subtitle="Which accounting solution is best for your growing business? See why smart businesses are making the switch."
        leftBrand={{
          label: "LEDGEROWL",
          className: "border-blue-primary-500/30 bg-blue-primary-500/[0.08]",
        }}
        rightBrand={{
          label: "OUTSOURCING",
          className: "border-gray-cool-400/30 bg-gray-cool-400/[0.06] text-gray-cool-400",
        }}
      />

      <ComparisonIntro
        heading={
          <>
            A <span className="text-blue-primary-500">quick breakdown</span>
          </>
        }
        paragraphs={[
          'Outsourced bookkeepers are manual and reactive, typically delivering books 15+ days after month-end. Ledgerowl\'s platform provides services in real-time, giving you financial visibility built for scale.',
        ]}
        cards={introCards}
      />

      <ComparisonDifferences
        title="Core differences"
        subtitle="The fundamental ways Ledgerowl and outsourced bookkeeping differ for growing Australian businesses"
        cards={coreDifferences}
      />

      <ComparisonTable
        title={
          <>
            Why leading businesses choose{" "}
            <span className="text-blue-primary-500">Ledgerowl</span>
          </>
        }
        subtitle="A side-by-side comparison of what you get with each approach"
        leftHeader="Ledgerowl"
        rightHeader="Outsourcing"
        rows={comparisonRows}
        variant="dark"
      />

      <ComparisonPricing
        title="Pricing"
        subtitle="Outsourced bookkeeper pricing typically only covers services — you still need to pay separately for accounting software and integration tools. Ledgerowl offers transparent, all-inclusive pricing."
        leftCard={{
          brand: "Ledgerowl",
          tagline: "All-inclusive pricing",
          price: "$299",
          priceSuffix: "/month",
          priceNote: "Starting from — includes everything",
          features: [
            "Software + expert services bundled",
            "Unlimited integrations included",
            "Real-time financial dashboard",
            "Dedicated accounting team",
            "AI-powered automation",
            "Compliance & tax-ready reports",
          ],
        }}
        rightCard={{
          brand: "Outsourcing",
          tagline: "Services only — software extra",
          price: "$400–$5,000+",
          priceSuffix: "/month",
          features: [
            { icon: "💸", text: "Bookkeeping services only" },
            { icon: "💸", text: "+ Xero/QBO subscription ($30-200/mo)" },
            { icon: "💸", text: "+ Integration tools ($50-300/mo)" },
            { icon: "⚠️", text: "Reports delayed 15+ days" },
            { icon: "⚠️", text: "Manual data entry & reconciliation" },
            { icon: "⚠️", text: "Separate tax prep required" },
          ],
        }}
      />

      <ComparisonTestimonial
        quote="Switching to Ledgerowl transformed the way I use my financials in my day-to-day operations. <strong>Real-time visibility into our numbers has been a game-changer</strong> for decision making."
        authorInitials="ST"
        authorName="Sarah T."
        authorRole="Founder & CEO, Growing SME"
      />

      <ComparisonBenefits
        title="What you get with Ledgerowl"
        subtitle="AI speed with human trust — everything you need to keep your books flawless and your business growing"
        benefits={benefits}
      />

      {/* Integrations */}
      <section className="py-[100px] px-6 lg:px-10 bg-base-white">
        <div className="max-w-[1100px] mx-auto">
          <h2 className="font-inter text-[24px] sm:text-[32px] lg:text-[38px] font-black tracking-[-1.5px] leading-[1.15] mb-4 text-center fade-in">
            We integrate with the{" "}
            <span className="text-blue-primary-500">best platforms</span>
          </h2>
          <p className="font-inter text-base text-gray-cool-500 leading-[1.7] max-w-[700px] mx-auto mb-10 text-center fade-in">
            Connect your entire financial ecosystem in minutes.
          </p>
          <div className="flex gap-4 flex-wrap justify-center fade-in">
            {integrationLogos.map((item) => (
              <div
                key={item.name}
                className="bg-cream border border-gray-cool-200 rounded-xl py-4 px-7 flex items-center gap-2 transition-all duration-200 hover:border-blue-primary-500 hover:text-blue-primary-500 hover:-translate-y-0.5"
              >
                <Image
                  src={item.logo}
                  alt={item.name}
                  width={item.width}
                  height={item.height}
                  className="h-[24px] w-auto object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <ComparisonFAQ
        title="FAQs"
        subtitle="Everything you need to know about switching from outsourced bookkeeping to Ledgerowl"
        items={faqItems}
      />

      <CallToAction />

      <Footer />
    </>
  );
}
